<template>
    <ion-page>
        <base-header :title="`Contratto ${contract?.serial_number}-${contract?.serial_suffix}`" />
        <ion-content v-if="!loading" :fullscreen="true">
            <ion-header collapse="condense">
                <ion-toolbar>
                    <ion-title size="large">
                        {{ `Contratto ${contract?.serial_number}-${contract?.serial_suffix}` }}
                    </ion-title>
                </ion-toolbar>
            </ion-header>
            <ion-buttons v-if="isOnline && contract" class="ion-padding-horizontal flex justify-end">
                <ion-button
                    fill="clear"
                    @click="
                        router.push({
                            name: 'contractForm',
                            params: { costumerId: contract?.costumer, contractId: contract?.id },
                        })
                    ">
                    <ion-text color="primary">
                        Modifica
                        <ion-icon :icon="createOutline"></ion-icon>
                    </ion-text>
                </ion-button>
            </ion-buttons>
            <template v-if="contract">
                <div class="ion-margin-vertical ion-padding-horizontal">
                    <div
                        style="border-radius: 8px; padding: 8px; background-color: var(--ion-color-step-150)"
                        class="flex justify-between items-center ion-margin-bottom-half">
                        <ion-text class="inspection-details-date-title">
                            <b>Stampa contratto</b>
                        </ion-text>
                        <ion-buttons>
                            <ion-button
                                :disabled="!isOnline || isDocumentLoading"
                                fill="clear"
                                @click="downloadContract"
                                size="large"
                                color="primary">
                                <ion-icon :icon="downloadOutline"></ion-icon>
                            </ion-button>
                            <ion-button
                                :disabled="!isOnline || isDocumentLoading"
                                fill="clear"
                                @click="sendContractMail"
                                size="large"
                                color="primary">
                                <ion-icon v-if="!isShareContractPrintLoading" :icon="mailOutline"></ion-icon>
                                <ion-spinner v-else></ion-spinner>
                            </ion-button>
                        </ion-buttons>
                    </div>
                    <div
                        style="border-radius: 8px; padding: 8px; background-color: var(--ion-color-step-150)"
                        class="flex justify-between items-center ion-margin-bottom-half">
                        <ion-text class="inspection-details-date-title">
                            <b>Stampa programmazione annuale</b>
                        </ion-text>
                        <ion-buttons>
                            <ion-button
                                :disabled="!isOnline || isDocumentLoading"
                                fill="clear"
                                @click="downloadContractYearPlan"
                                size="large"
                                color="primary">
                                <ion-icon :icon="downloadOutline"></ion-icon>
                            </ion-button>
                            <ion-button
                                :disabled="!isOnline || isDocumentLoading"
                                fill="clear"
                                @click="sendYearPlanningMail"
                                size="large"
                                color="primary">
                                <ion-icon v-if="!isShareContractYearPlanLoading" :icon="mailOutline"></ion-icon>
                                <ion-spinner v-else></ion-spinner>
                            </ion-button>
                        </ion-buttons>
                    </div>
                    <div
                        style="border-radius: 8px; padding: 8px; background-color: var(--ion-color-step-150)"
                        class="flex justify-between items-center">
                        <ion-text class="inspection-details-date-title">
                            <b>Stampa relazione finale</b>
                        </ion-text>
                        <ion-buttons>
                            <ion-button
                                :disabled="!isOnline || isDocumentLoading"
                                fill="clear"
                                @click="downloadContractFinalReport"
                                size="large"
                                color="primary">
                                <ion-icon :icon="downloadOutline"></ion-icon>
                            </ion-button>
                            <ion-button
                                :disabled="!isOnline || isDocumentLoading"
                                fill="clear"
                                @click="sendFinalReportMail"
                                size="large"
                                color="primary">
                                <ion-icon v-if="!isShareFinalReportLoading" :icon="mailOutline"></ion-icon>
                                <ion-spinner v-else></ion-spinner>
                            </ion-button>
                        </ion-buttons>
                    </div>
                    <div v-if="!isOnline" style="text-align: right; padding-top: 4px">
                        <ion-text color="danger"><small>La stampa non è disponibile offline</small></ion-text>
                    </div>
                </div>
            </template>
            <div class="ion-padding-horizontal ion-margin-top">
                <ion-text color="medium">Data di compilazione</ion-text>
                : {{ dateFormat(contract?.date ?? "", "2-digit", "2-digit", "numeric") }}
            </div>
            <div class="ion-padding-horizontal ion-margin-top">
                <ion-text color="medium">Periodo di validità</ion-text>
                :
                <br />
                &nbsp;&nbsp;&nbsp;&nbsp;{{
                    dateFormat(contract?.start_validity ?? "", "numeric", "2-digit", "2-digit")
                }}
                - {{ dateFormat(contract?.end_validity ?? "", "numeric", "2-digit", "2-digit") }}
                <ion-note>
                    ({{ dateDeltaFormat(contract?.start_validity ?? "", contract?.end_validity ?? "") }})
                </ion-note>
            </div>
            <div class="w-full ion-margin-top-half ion-padding-horizontal">
                <ion-text color="medium">Servizi</ion-text>
                :
                <ul>
                    <li v-if="contract?.monitoring_rodent">Monitoraggio roditori</li>
                    <li v-if="contract?.monitoring_crawling">Monitoraggio insetti striscianti - disinfestazione</li>
                    <li v-if="contract?.monitoring_flying">Monitoraggio insetti volanti</li>
                    <li v-if="contract?.monitoring_flours_pests">Monitoraggio insetti delle derrate</li>
                    <li v-if="contract?.haccp">H.A.C.C.P.</li>
                </ul>
            </div>
            <div class="ion-padding-horizontal ion-margin-top">
                <div class="w-full">
                    <ion-text color="medium">Frequenza di intervento</ion-text>
                    :
                    <span v-if="interventionFrequency">
                        {{ interventionFrequency.description }}
                    </span>
                    <ion-note v-else>
                        <br />
                        &nbsp;&nbsp;&nbsp;&nbsp;
                        <small><em>Nessuna frequenza di intervento definita</em></small>
                    </ion-note>
                </div>
                <div v-if="interventionFrequency">
                    <div>
                        <small>
                            <ion-text color="medium">Interventi annuali</ion-text>
                            : {{ interventionFrequency.year_interventions_amount }}
                        </small>
                    </div>
                    <div>
                        <small>
                            <ion-text color="medium">Estremi intervalli di scadenza</ion-text>
                            : {{ interventionFrequency.recent_inspection_interval }} -
                            {{ interventionFrequency.regular_situation_interval }} -
                            {{ interventionFrequency.expiring_situation_interval }}
                        </small>
                    </div>
                </div>
            </div>
            <div class="ion-padding-horizontal ion-margin-top">
                <ion-text color="medium">Città</ion-text>
                : {{ contract?.city }}
            </div>
            <ion-card v-if="contract" class="ion-margin-top">
                <ion-card-header>
                    <ion-card-title>Pianificazione degli interventi</ion-card-title>
                </ion-card-header>

                <ion-card-content>
                    <span class="block">
                        In base alla frequenza di interventi impostata per il cliente al momento della creazione del
                        contratto, sono state calcolate le seguenti scadenze:
                    </span>
                    <ion-list lines="full" class="ion-margin-top-half">
                        <ion-item
                            v-for="(deadline, index) in contract.deadlines"
                            style="--background: var(--ion-card-background)">
                            <div slot="start">
                                <strong style="font-size: 1.2rem">{{ index + 1 }}</strong>
                                .
                            </div>
                            <ion-label>
                                {{ dateFormat(deadline.split("|")[0], null, "short") }} -
                                {{ dateFormat(deadline.split("|")[1], "numeric", "short") }}
                            </ion-label>
                        </ion-item>
                    </ion-list>
                </ion-card-content>
            </ion-card>
        </ion-content>
        <ion-content v-else>
            <ion-list>
                <ion-item class="costumer-list-item" v-for="_ in [1, 2, 3, 4, 5, 6, 7, 8, 9]">
                    <ion-label>
                        <h3>
                            <ion-skeleton-text :animated="true" style="width: 80%"></ion-skeleton-text>
                        </h3>
                        <p>
                            <ion-skeleton-text :animated="true" style="width: 60%"></ion-skeleton-text>
                        </p>
                        <p>
                            <ion-skeleton-text :animated="true" style="width: 30%"></ion-skeleton-text>
                        </p>
                    </ion-label>
                </ion-item>
            </ion-list>
        </ion-content>
    </ion-page>
</template>

<script setup lang="ts">
import {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonNote,
    IonPage,
    IonSkeletonText,
    IonText,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonTitle,
    IonToolbar,
    IonSpinner,
    toastController,
} from "@ionic/vue";
import BaseHeader from "@/components/BaseHeader.vue";

import { useRoute } from "vue-router";
import { useCostumersStore } from "@/stores/costumers";
import { useContractsStore } from "@/stores/contracts";
import { computed, onMounted, ref } from "vue";
import { ICostumer } from "@/types/costumer";
import { IContract } from "@/types/contract";
import { createOutline, downloadOutline, mailOutline } from "ionicons/icons";
import { useNetworkStore } from "@/stores/network";
import { dateDeltaFormat, dateFormat } from "@/utility";
import router from "@/router";
import axios, { AxiosResponse } from "axios";
import { useInterventionFrequencyStore } from "@/stores/interventionFrequencies";
import {
    baseURL,
    contractPrintFinalReportUrl,
    contractPrintUrl,
    contractPrintYearPlanUrl,
    contractSendFinalReportUrl,
    contractSendUrl,
    contractSendYearPlaUrl,
    inspectionPrintUrl,
} from "@/services/api";

const route = useRoute();
const costumersStore = useCostumersStore();
const networkStore = useNetworkStore();
const contractsStore = useContractsStore();
const interventionFrequenciesStore = useInterventionFrequencyStore();

const costumerId = parseInt(route.params.costumerId as string);
const contractId = parseInt(route.params.contractId as string);

const interventionFrequency = computed(() =>
    costumer.value?.intervention_frequency
        ? interventionFrequenciesStore.getInterventionFrequencyById(costumer.value?.intervention_frequency)
        : null
);

const isOnline = computed(() => networkStore.isOnline);
const loading = ref<boolean>(false);

const isDocumentLoading = ref<boolean>(false);
const isShareContractPrintLoading = ref<boolean>(false);
const isShareContractYearPlanLoading = ref<boolean>(false);
const isShareFinalReportLoading = ref<boolean>(false);

const costumer = computed<ICostumer | undefined>(() =>
    costumerId ? costumersStore.getCustomerById(costumerId) : undefined
);
const contract = computed<IContract | undefined>(() =>
    contractId ? contractsStore.getContractById(contractId) : undefined
);

const downloadPDF = (url: string) => {
    if (url != "") {
        let link = document.createElement("a");
        link.href = url;
        if (link.href) {
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }
};

const downloadContract = () => {
    isDocumentLoading.value = true;
    const url = contract.value ? baseURL + contractPrintUrl(contract.value!.id!) : "";
    downloadPDF(url);
    isDocumentLoading.value = false;
};

const downloadContractYearPlan = () => {
    isDocumentLoading.value = true;
    const url = contract.value ? baseURL + contractPrintYearPlanUrl(contract.value!.id!) : "";
    downloadPDF(url);
    isDocumentLoading.value = false;
};

const downloadContractFinalReport = () => {
    isDocumentLoading.value = true;
    const url = contract.value ? baseURL + contractPrintFinalReportUrl(contract.value!.id!) : "";
    downloadPDF(url);
    isDocumentLoading.value = false;
};

const sendMalHandler = (response: AxiosResponse<any, any>) => {
    if (response.status == 200) {
        toastController
            .create({
                message: "Mail inviata correttamente",
                duration: 3000,
                position: "top",
                color: "success",
            })
            .then((toast) => toast.present());
    } else {
        toastController
            .create({
                message: "Invio mail fallito",
                duration: 3000,
                position: "top",
                color: "danger",
            })
            .then((toast) => toast.present());
    }
};

const sendContractMail = async () => {
    isDocumentLoading.value = true;
    isShareContractPrintLoading.value = true;
    axios
        .get(baseURL + contractSendUrl(contract.value?.id || 0))
        .then(sendMalHandler)
        .finally(() => {
            isDocumentLoading.value = false;
            isShareContractPrintLoading.value = false;
        });
};

const sendYearPlanningMail = async () => {
    isDocumentLoading.value = true;
    isShareContractYearPlanLoading.value = true;
    axios
        .get(baseURL + contractSendYearPlaUrl(contract.value?.id || 0))
        .then(sendMalHandler)
        .finally(() => {
            isDocumentLoading.value = false;
            isShareContractYearPlanLoading.value = false;
        });
};

const sendFinalReportMail = async () => {
    isDocumentLoading.value = true;
    isShareFinalReportLoading.value = true;
    axios
        .get(baseURL + contractSendFinalReportUrl(contract.value?.id || 0))
        .then(sendMalHandler)
        .finally(() => {
            isDocumentLoading.value = false;
            isShareFinalReportLoading.value = false;
        });
};
</script>

<style scoped></style>
